import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bell, Warning, Clock, CalendarBlank, X, ArrowRight } from '@phosphor-icons/react';

const API_URL = (typeof window !== 'undefined' && window.location.hostname && !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  overdue: { icon: Warning, color: 'text-[#dc2626]', bg: 'bg-[#fef2f2]', border: 'border-[#fecaca]', label: 'Overdue' },
  today: { icon: Clock, color: 'text-[#f59e0b]', bg: 'bg-[#fef3c7]', border: 'border-[#fde68a]', label: 'Due Today' },
  tomorrow: { icon: CalendarBlank, color: 'text-[#0ea5e9]', bg: 'bg-[#e0f2fe]', border: 'border-[#bae6fd]', label: 'Tomorrow' },
  upcoming: { icon: CalendarBlank, color: 'text-[#6b7280]', bg: 'bg-[#f3f4f6]', border: 'border-[#e5e7eb]', label: 'Upcoming' },
};

export const NotificationPanel = ({ open, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open]);

  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/notifications/history`, { withCredentials: true });
      setNotifications(res.data.notifications || []);
    } catch { /* not logged in */ }
    finally { setLoading(false); }
  };

  if (!open) return null;

  const overdueCount = notifications.filter(n => n.type === 'overdue').length;
  const todayCount = notifications.filter(n => n.type === 'today').length;

  return (
    <div ref={panelRef} className="fixed left-72 top-4 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-[#f2f4f6] z-[70] overflow-hidden max-[1023px]:left-4 max-[1023px]:right-4 max-[1023px]:w-auto" data-testid="notification-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#f2f4f6]">
        <div className="flex items-center gap-2">
          <Bell size={16} weight="fill" className="text-[#2d3338]" />
          <span className="font-bold text-sm text-[#2d3338]">Notifications</span>
          {(overdueCount + todayCount) > 0 && (
            <span className="text-[10px] bg-[#dc2626] text-white px-1.5 py-0.5 rounded-full font-bold">
              {overdueCount + todayCount}
            </span>
          )}
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#f2f4f6] transition-colors">
          <X size={14} weight="bold" className="text-[#596065]" />
        </button>
      </div>

      {/* Notifications list */}
      <div className="max-h-[400px] overflow-y-auto">
        {loading ? (
          <div className="p-6 text-center">
            <div className="w-6 h-6 border-2 border-[#e2e8f0] border-t-[#005bc1] rounded-full animate-spin mx-auto" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center">
            <Bell size={32} className="mx-auto mb-2 text-[#d1d5db]" weight="duotone" />
            <p className="text-sm font-medium text-[#2d3338]">All clear!</p>
            <p className="text-xs text-[#596065] mt-1">No upcoming due dates this week</p>
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {notifications.map((notif, i) => {
              const config = TYPE_CONFIG[notif.type] || TYPE_CONFIG.upcoming;
              const Icon = config.icon;
              return (
                <div
                  key={i}
                  className={`flex items-start gap-3 p-3 rounded-xl ${config.bg} border ${config.border} transition-colors`}
                  data-testid={`notif-${notif.type}-${i}`}
                >
                  <div className={`w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0 ${config.color}`}>
                    <Icon size={16} weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#2d3338] truncate">{notif.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-xs font-medium ${config.color}`}>{notif.message}</span>
                      {notif.amount && (
                        <span className="text-xs text-[#596065]">{notif.amount}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="border-t border-[#f2f4f6] px-4 py-2.5">
          <a href="/inbox" className="flex items-center justify-center gap-1 text-xs font-semibold text-[#005bc1] hover:underline" data-testid="view-all-tasks-link">
            View all tasks <ArrowRight size={12} weight="bold" />
          </a>
        </div>
      )}
    </div>
  );
};
